import { DateCell, Inline, Skeleton } from '@omnipresentgroup/design-system';
import type { Employment } from '@omnipresentgroup/types';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import { EmploymentStatusTag } from '../../employments-status-tag';
import { EmploymentActionsCell, LegalEntityName } from '../components';

export enum ColumnHeaders {
  startDate = 'Start date',
  terminationDate = 'Termination date',
  status = 'Status',
  legalEntityId = 'Employer',
  type = 'Type',
  firstPayrollDate = 'First payroll',
  lastPayrollDate = 'Last payroll',
  actions = '',
}

export const useEmploymentsTableConfig = ({
  columnsOverride,
  onEditClick,
}: {
  columnsOverride?: ColumnDef<Employment, unknown>[];
  onEditClick?: (row: Employment) => void;
}) => {
  const columnHelper = useMemo(() => createColumnHelper<Employment>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('status', {
        cell: (info) => <EmploymentStatusTag employment={info?.row.original} />,
        header: () => ColumnHeaders.status,
        size: 90,
      }),
      columnHelper.accessor('startDate', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.startDate,
        size: 90,
      }),
      columnHelper.accessor('type', {
        cell: (info) => info?.getValue() ?? '-',
        header: () => ColumnHeaders.type,
      }),
      columnHelper.accessor('legalEntityId', {
        cell: (info) => <LegalEntityName legalEntityId={info.getValue()} />,
        header: () => ColumnHeaders.legalEntityId,
      }),
      columnHelper.accessor('firstPayrollDate', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.firstPayrollDate,
        size: 90,
      }),
      columnHelper.accessor('terminationDate', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.terminationDate,
        size: 90,
      }),
      columnHelper.accessor('lastPayrollDate', {
        cell: (info) => <DateCell value={info.getValue()} />,
        header: () => ColumnHeaders.lastPayrollDate,
        size: 90,
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => (
          <EmploymentActionsCell
            employment={info.row.original}
            onEditClick={onEditClick}
          />
        ),
        header: () => ColumnHeaders.actions,
        size: 24,
        enableSorting: false,
        meta: {
          onClickStopPropagation: true,
          loader: (
            <Inline gap="8" justify="flex-end">
              <Skeleton height={16} width={24} />
            </Inline>
          ),
        },
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride, onEditClick],
  );

  return { columns };
};
